import React, { useEffect, useRef } from 'react';
import Moveable from 'react-moveable';
import { useDispatch, useSelector } from 'react-redux';
import { sortBy, isEqual } from 'lodash-es';
import {
  getFormEditorControlData,
  getSelectedFormEditorForm,
  onInputFieldChange,
  selectInputField,
  toggleSelectInputField,
} from '../../store/slices/formEditorSlice';
import { selectSelectedProjectResources } from 'store/resources/selectors/resources.selectors';

export const InputFieldsNew = ({
  selectedPageIndex = 0,
  isLandscape = false,
  containerWidth = 595,
  containerHeight = 842,
}) => {
  const containerRef = useRef(null);
  const boxRefs = useRef({});
  const dispatch = useDispatch();
  const selectedForm = useSelector(getSelectedFormEditorForm);
  const formEditorControlData = useSelector(getFormEditorControlData);

  const handleClick = (e, id) => {
    e.stopPropagation();
    if (e.ctrlKey || e.metaKey) {
      dispatch(toggleSelectInputField({ id }));
    } else {
      dispatch(selectInputField({ id }));
    }
  };

  const normalizeIndexes = (pageInputFields) => {
    const sortedFields = sortBy(pageInputFields, 'originalIndex');
    return sortedFields.map((field, index) => ({
      ...field,
      originalIndex: index + 1,
    }));
  };

  useEffect(() => {
    if (!selectedForm?.inputFields?.[selectedPageIndex]) return;

    const currentFields = selectedForm.inputFields[selectedPageIndex];
    const normalizedFields = normalizeIndexes(currentFields);

    const needsUpdate = !isEqual(
      currentFields.map((f) => f.originalIndex),
      normalizedFields.map((f) => f.originalIndex)
    );

    if (needsUpdate) {
      normalizedFields.forEach((field) => {
        dispatch(
          onInputFieldChange({
            updatedInputField: field,
            pageIndex: selectedPageIndex,
          })
        );
      });
    }
  }, [
    selectedForm?.inputFields?.[selectedPageIndex]?.length,
    selectedPageIndex,
  ]);

  const capturePosition = () => {
    if (!formEditorControlData?.selectedInputFieldId) return;

    const el = boxRefs.current[formEditorControlData?.selectedInputFieldId];
    if (!el) return;

    const x = parseFloat(el.style.left);
    const y = parseFloat(el.style.top);
    const width = el.offsetWidth;
    const height = el.offsetHeight;

    const original = selectedForm?.inputFields?.[selectedPageIndex]?.find(
      (item) => item.id === formEditorControlData?.selectedInputFieldId
    );
    if (!original) return;

    const updated = {
      ...original,
      x,
      y,
      width,
      height,
    };

    dispatch(
      onInputFieldChange({
        updatedInputField: updated,
        pageIndex: selectedPageIndex,
      })
    );
  };

  const radioGroups = React.useMemo(() => {
    return (
      selectedForm?.inputFields?.[selectedPageIndex]?.reduce((groups, item) => {
        if (item.type?.name === 'radioButton' && item.parentTitle) {
          const groupKey =
            typeof item.parentTitle === 'object'
              ? item.parentTitle.title
              : item.parentTitle;
          if (!groups[groupKey]) {
            groups[groupKey] = [];
          }
          groups[groupKey].push(item);
        }
        return groups;
      }, {}) || {}
    );
  }, [selectedForm, selectedPageIndex]);

  return (
    <div
      ref={containerRef}
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
      }}
    >
      <svg
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          pointerEvents: 'none',
          zIndex: 0,
        }}
      >
        {Object.values(radioGroups).flatMap((group, groupIndex) => {
          if (group.length < 2) return [];
          return group.slice(1).map((item, index) => {
            const prevItem = group[index];
            return (
              <line
                key={`${groupIndex}-${index}`}
                x1={prevItem.x + prevItem.width / 2}
                y1={prevItem.y + prevItem.height / 2}
                x2={item.x + item.width / 2}
                y2={item.y + item.height / 2}
                stroke="#4ad3ff"
                strokeWidth="2"
                strokeDasharray="5,5"
                opacity="0.7"
              />
            );
          });
        })}
      </svg>
      {selectedForm?.inputFields?.[selectedPageIndex]?.map((item, index) => (
        <div
          key={item.id}
          ref={(el) => (boxRefs.current[item.id] = el)}
          onClick={(e) => handleClick(e, item.id)}
          style={{
            position: 'absolute',
            top: `${item.y}px`,
            left: `${item.x}px`,
            width: `${item.width}px`,
            height: `${item.height}px`,
            backgroundColor: `rgba(20, 123, 164, ${formEditorControlData.inputFieldOpacity})`,
            color: 'black',
            padding: '6px',
            border: formEditorControlData.selectedInputFieldIds?.includes(item.id)
              ? '2px solid #79ffe1'
              : '1px solid #004d6b',
            boxSizing: 'border-box',
            cursor: 'move',
            userSelect: 'none',
            zIndex:
              formEditorControlData?.selectedInputFieldId === item.id ? 10 : 1,
            fontSize: `${item.textSize ?? 14}px`,
          }}
        >
          <div
            style={{
              position: 'absolute',
              top: '-8px',
              left: '-8px',
              width: '20px',
              height: '20px',
              backgroundColor: '#4ad3ff',
              color: '#0f1220',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '10px',
              fontWeight: 'bold',
              border: '2px solid #fff',
              zIndex: 1,
            }}
          >
            {(() => {
              if (item.parentTitle && typeof item.parentTitle === 'object') {
                return `${item.originalIndex || index + 1}.${item.parentTitle.groupNumber
                  }`;
              }
              return item.originalIndex || index + 1;
            })()}
          </div>
          {item.text}
          {(item.type?.name === 'radioButton' ||
            item.type?.name === 'checkBox') && (
              <div
                style={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  fontSize: '16px',
                  fontWeight: 'bold',
                  color: '#004d6b',
                  pointerEvents: 'none',
                }}
              >
                ✕
              </div>
            )}
        </div>
      ))}

      {formEditorControlData?.selectedInputFieldId && (
        <Moveable
          target={boxRefs.current[formEditorControlData?.selectedInputFieldId]}
          container={null}
          draggable
          resizable
          bounds={{
            left: 0,
            top: 0,
            right: containerWidth,
            bottom: containerHeight,
          }}
          onDrag={({ target, left, top }) => {
            target.style.left = `${left}px`;
            target.style.top = `${top}px`;
          }}
          onDragEnd={capturePosition}
          onResize={({ target, width, height, drag }) => {
            target.style.width = `${width}px`;
            target.style.height = `${height}px`;
            target.style.transform = drag.transform;
          }}
          onResizeEnd={() => {
            capturePosition();
          }}
          renderDirections={['e', 's', 'se']}
          keepRatio={false}
          snappable
          originDraggable={true}
          originRelative={false}
        />
      )}
      <style>
        {`
          ${(() => {
            const selectedItem = selectedForm?.inputFields?.[
              selectedPageIndex
            ]?.find(
              (item) => item.id === formEditorControlData?.selectedInputFieldId
            );
            if (
              selectedItem?.type?.name === 'radioButton' ||
              selectedItem?.type?.name === 'checkBox'
            ) {
              return '.moveable-origin { display: none !important; }';
            }
            return '';
          })()}
        `}
      </style>
    </div>
  );
};
